// A round's own price list (Your prices, 2026-10-01) and the one sum that
// turns it into a price for a job. Pure: no database, no screen. The owner's
// screen uses it for the live example, the server uses it to check a saved
// list, and the email quote drafts will use it later, so all three always
// agree on a price. The AI never sets a price: it only fills in the job.
//
// Money is whole pence and every percentage a whole number (90 = 90%), so
// the same list always gives the same price on every device.
//
// Plan: docs/plans/next-level/plan.html. Dropby's own rules, which the tests
// replay: ~/Desktop/Sites/Dropby/CLAUDE.md "Pricing config".

// by_area: a price a square metre for each kind of job, for patio, roof and driveway work (Liam picked A, 15 Oct 2026:
// https://claude.ai/artifact/FLSJyvYH1LanhJSJdu5Xnt). The three kinds are the visit's own (cleans.kind window / gutter /
// other), in the round's words: Patio and driveway, Roof and gutters, Other service.
export type PriceMethod = 'per_window' | 'house_size' | 'by_area'
export type JobKind = 'window' | 'gutter' | 'other'
export type SizeRates = Record<JobKind, number | null>
export type HowOften = 'one_off' | 'w2' | 'w4' | 'w6' | 'w8' | 'w12'
export type RoundMode = 'up' | 'nearest'

export const HOW_OFTEN: { value: HowOften; label: string }[] = [
  // 2 and 6 weeks (Liam, 27 Oct 2026: "Go with A", their own discounts, starting the same as 4 and 8 weeks).
  { value: 'w2', label: 'Every 2 weeks' },
  { value: 'w4', label: 'Every 4 weeks' },
  { value: 'w6', label: 'Every 6 weeks' },
  { value: 'w8', label: 'Every 8 weeks' },
  { value: 'w12', label: 'Every 12 weeks' },
  { value: 'one_off', label: 'One-off' },
]

export interface AreaPrice {
  // Postcode starts that get this window price, e.g. ["N10", "N8"]. For the
  // owner only: a customer is never told their price depends on the area.
  starts: string[]
  window_pence: number
}

export interface HouseSize {
  label: string
  pence: number
}

export const PRICE_LIST_SCHEMA = 1

export interface PriceList {
  schema: number // PRICE_LIST_SCHEMA when saved; lets a later shape be read safely
  method: PriceMethod
  window_pence: number | null // per_window
  area_prices: AreaPrice[] // per_window
  sizes: HouseSize[] // house_size
  // by_area: pence a square metre for each kind of job; null = that kind is not priced this way. Lists saved before
  // 15 Oct 2026 have none: read it with sizeRates().
  size_rates?: SizeRates
  first_clean_extra_pct: number // 25 = the first clean costs 25% more
  first_clean_gets_discount: boolean // does the how-often discount apply to the first clean?
  // The first-clean extra goes on top of the smallest job too, so a £25 visit's first clean at 40% more is £35 (Liam,
  // 16 Oct 2026: "minimum call out is £25", "£35 for first clean"). Off, or missing on a list saved before: the extra is
  // added before the smallest job price, and a small job's first clean may be the smallest job price itself.
  first_clean_over_minimum?: boolean
  inside_pct: number // 150 = inside costs 1.5 times the regular outside price
  // Each Velux or roof window. null = priced like any other window (per window) or not extra (by house size):
  // the set-up's "Same as a window" answer (1 Oct).
  roof_window_pence: number | null
  how_often_pct: Record<HowOften, number> // 90 = 10% off
  minimum_pence: number | null // the least a visit costs
  fronts_minimum_pence: number | null // the least a fronts-only visit costs
  round_to_pence: number // 0 = don't round, 500 = to £5
  round_mode: RoundMode
}

export interface Job {
  windows?: number | null // per_window
  size?: string | null // house_size: one of the list's labels
  postcode?: string | null
  roof_windows?: number | null
  sqm?: number | null // by_area: the job's size in square metres
  kind?: JobKind | null // by_area: which kind of job (the visit's kind); window when not said
  how_often: HowOften
  fronts_only?: boolean
}

// priced: every fact the price needs is there. needs_details: something
// that changes the price is missing (window count, house size), so no price
// may be sent. Ranges come back as priced with regular_range_pence set.
export type QuoteStatus = 'priced' | 'needs_details'

export interface Quote {
  status: QuoteStatus
  regular_pence: number // each visit after the first, outside
  first_pence: number // the first clean, outside
  inside_pence: number | null // inside as well, each visit; null = the list has no inside price
  lines: string[] // how the price was worked out, for the owner
  // A different price applies in some areas and the postcode is missing or
  // matches none of them: the regular price could be anything in this range.
  regular_range_pence: [number, number] | null
  first_range_pence: [number, number] | null // set whenever regular_range_pence is
  inside_range_pence: [number, number] | null // set whenever regular_range_pence is and insides are priced
  missing: ('windows' | 'size' | 'sqm')[] // what the job still needs before it can be priced
}

const MAX_PENCE = 1_000_000 // £10,000: nothing on a window round costs more
const MAX_PCT = 1000
const MAX_SQM = 10_000 // the largest job priced by its size

export function blankPriceList(method: PriceMethod): PriceList {
  return {
    schema: PRICE_LIST_SCHEMA,
    method,
    window_pence: null,
    area_prices: [],
    sizes: method === 'house_size' ? ['Flat', '2 bed', '3 bed', '4 bed', '5 bed or more'].map((label) => ({ label, pence: 0 })) : [],
    size_rates: { window: null, gutter: null, other: null },
    first_clean_extra_pct: 0,
    first_clean_gets_discount: false,
    inside_pct: 0,
    roof_window_pence: null,
    how_often_pct: { one_off: 100, w2: 100, w4: 100, w6: 100, w8: 100, w12: 100 },
    minimum_pence: null,
    fronts_minimum_pence: null,
    round_to_pence: 0,
    round_mode: 'up',
  }
}

// A list's rates a square metre, empty for a list saved before by_area existed.
export function sizeRates(list: PriceList): SizeRates {
  const r = list.size_rates
  return { window: r?.window ?? null, gutter: r?.gutter ?? null, other: r?.other ?? null }
}

// The outward half of a UK postcode, upper case: "n10 3ab" -> "N10",
// "N103AB" -> "N10", "N10" -> "N10". A US ZIP is its five numbers ("10001-1234" -> "10001"); a Canadian postal code
// its first three, as the UK's outward half ("m5v 3l9" -> "M5V"; Step 2 round 3). Anything else -> null.
// A UK round reads only UK postcodes, exactly as before; a US round only ZIPs; a Canadian round its own codes.
export function postcodeStart(postcode: string | null | undefined, country = 'GB'): string | null {
  if (!postcode) return null
  const s = postcode.toUpperCase().replace(/\s+/g, '')
  if (country === 'US') return /^\d{5}(-?\d{4})?$/.test(s) ? s.slice(0, 5) : null
  if (country === 'CA') return /^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(s) ? s.slice(0, 3) : null
  if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(s)) return s.slice(0, -3)
  if (/^[A-Z]{1,2}\d[A-Z\d]?$/.test(s)) return s
  return null
}

// What an area price is made of, in the round's country's words (Step 2 round 3).
const AREA_WORDS: Record<string, { none: string; bad: (t: string) => string }> = {
  GB: { none: 'Each area price needs at least one postcode start, like N10.', bad: (t) => `${t} isn't the start of a postcode. Use the first half, like N10 or EN2.` },
  US: { none: 'Each area price needs at least one ZIP code, like 10001.', bad: (t) => `${t} isn't a ZIP code. Use its five numbers, like 10001.` },
  CA: { none: 'Each area price needs at least one postal code start, like M5V.', bad: (t) => `${t} isn't the start of a postal code. Use the first three, like M5V.` },
}

// A how-often's share of the price. A list saved before 2 and 6 weeks existed has none for them: 2 weeks reads as 4
// weeks' and 6 weeks as 8 weeks', until the owner sets their own (27 Oct 2026).
export function oftenPct(list: Pick<PriceList, 'how_often_pct'>, h: HowOften): number {
  const t = list.how_often_pct as Partial<Record<HowOften, number>>
  const v = t[h] ?? (h === 'w2' ? t.w4 : h === 'w6' ? t.w8 : undefined)
  return typeof v === 'number' ? v : 100
}

function pct(pence: number, p: number): number {
  return Math.round((pence * p) / 100)
}

// Round a price, never below the floor: rounding to the nearest £5 would
// take a £37 minimum down to £35, so a floor the rounding undercuts is
// rounded up instead (Astra, 1 Oct).
function roundPence(pence: number, list: PriceList, floor: number | null = null): number {
  const step = list.round_to_pence
  if (!step) return pence
  const n = list.round_mode === 'up' ? Math.ceil(pence / step - 1e-9) : Math.round(pence / step)
  let out = Math.max(step, n * step)
  if (floor != null && out < floor) out = Math.ceil(floor / step - 1e-9) * step
  return out
}

// sym: the round's money mark (£, or $ for a US or Canadian round; Step 2).
function pounds(pence: number, sym = '£'): string {
  return `${sym}${(pence / 100).toFixed(pence % 100 === 0 ? 0 : 2)}`
}

// Price one visit from a base outside price (before how-often and minimum).
function visit(base: number, list: PriceList, job: Job, first: boolean): number {
  // A job priced by its size is its rate, the smallest job and rounding, nothing else: a first-clean extra or a
  // regulars' discount kept from a window list would change a price the owner never sees asked about (Codex, 15 Oct).
  const bySize = list.method === 'by_area'
  const over = first && !bySize && list.first_clean_over_minimum === true
  let p = first && !bySize && !over ? pct(base, 100 + list.first_clean_extra_pct) : base
  if (!bySize && (!first || list.first_clean_gets_discount)) p = pct(p, oftenPct(list, job.how_often))
  const floor = job.fronts_only ? (list.fronts_minimum_pence ?? list.minimum_pence) : list.minimum_pence
  if (floor != null) p = Math.max(p, floor)
  if (over) p = pct(p, 100 + list.first_clean_extra_pct)
  return roundPence(p, list, floor)
}

// A regular visit's price from a base outside price at one how-often: its share, the smallest job and rounding, exactly
// as quote() prices it (Book again, 0094: each tile's price from the customer's own).
export function regularVisitPrice(list: PriceList, base: number, howOften: HowOften): number {
  return visit(base, list, { how_often: howOften }, false)
}

// The window rate a job is priced at: its area's when the postcode is in one, else the list's own. No
// postcode while areas have their own price: range is the lowest and highest rate it could be. Exported
// for the quote reply (src/quote-message.ts), which names the rate that applied.
export function windowRate(list: PriceList, job: Job, country = 'GB'): { rate: number; range: [number, number] | null } {
  const base = list.window_pence ?? 0
  if (list.area_prices.length === 0) return { rate: base, range: null }
  const start = postcodeStart(job.postcode, country)
  if (start) {
    const hit = list.area_prices.find((a) => a.starts.some((s) => s.toUpperCase() === start))
    if (hit) return { rate: hit.window_pence, range: null }
    return { rate: base, range: null }
  }
  const rates = [base, ...list.area_prices.map((a) => a.window_pence)]
  return { rate: base, range: [Math.min(...rates), Math.max(...rates)] }
}

// country: the round's, for reading the job's postcode (a ZIP in the US; Step 2 round 3).
export function quote(list: PriceList, job: Job, sym = '£', country = 'GB'): Quote {
  const money = (pence: number): string => pounds(pence, sym)
  const lines: string[] = []
  const missing: Quote['missing'] = []
  const roof = Math.max(0, Math.floor(job.roof_windows ?? 0))
  // What the roof windows add at a given window rate.
  const roofAt = (rate: number): number =>
    list.roof_window_pence != null ? roof * list.roof_window_pence : list.method === 'per_window' ? roof * rate : 0

  let base = 0
  let lowBase: number | null = null
  let highBase: number | null = null
  if (list.method === 'per_window') {
    const windows = job.windows != null && job.windows > 0 ? Math.floor(job.windows) : null
    if (windows == null) missing.push('windows')
    const { rate, range } = windowRate(list, job, country)
    base = (windows ?? 0) * rate
    if (windows != null) lines.push(`${windows} windows at ${money(rate)} = ${money(base)}`)
    if (roof > 0) {
      base += roofAt(rate)
      lines.push(
        list.roof_window_pence != null
          ? `${roof} roof windows at ${money(list.roof_window_pence)} = ${money(roofAt(rate))}`
          : `${roof} roof windows at ${money(rate)}, like the others = ${money(roofAt(rate))}`,
      )
    }
    if (range && windows != null) {
      lowBase = windows * range[0] + roofAt(range[0])
      highBase = windows * range[1] + roofAt(range[1])
      lines.push(`No postcode in one of your price areas, so between ${money(range[0])} and ${money(range[1])} a window`)
    }
  } else if (list.method === 'by_area') {
    // A price a square metre for the job's kind, times its size (15 Oct 2026). No rate for that kind, or no size yet:
    // it needs details first, never a price of nothing.
    const kind: JobKind = job.kind === 'gutter' || job.kind === 'other' ? job.kind : 'window'
    const rate = sizeRates(list)[kind]
    // A size to one decimal place, up to 10,000 m², and a job that comes to something (never £0 or out of bounds).
    const sqm = job.sqm != null && Number.isFinite(job.sqm) ? Math.round(job.sqm * 10) / 10 : null
    const sized = sqm != null && sqm > 0 && sqm <= MAX_SQM ? sqm : null
    const total = sized != null && rate != null && rate > 0 ? Math.round(sized * rate) : 0
    if (total <= 0 || total > MAX_PENCE) missing.push('sqm')
    else {
      base = total
      lines.push(`${sized} m² at ${money(rate!)} a m² = ${money(base)}`)
    }
  } else {
    // A size with no price is not a free clean: it needs a price first (Astra, 1 Oct).
    const size = list.sizes.find((s) => s.label === job.size && s.pence > 0)
    if (!size) missing.push('size')
    base = size?.pence ?? 0
    if (size) lines.push(`${size.label}: ${money(base)}`)
    if (roof > 0 && list.roof_window_pence != null) {
      base += roofAt(0)
      lines.push(`${roof} roof windows at ${money(list.roof_window_pence)} = ${money(roofAt(0))}`)
    }
  }

  const regular = visit(base, list, job, false)
  const first = visit(base, list, job, true)
  // Inside is worked out from the regular visit price the customer pays
  // (after the how-often discount, minimum and rounding), as Dropby's rule
  // reads: "1.5x the regular outside price".
  const inside = list.inside_pct > 0 && list.method !== 'by_area' ? roundPence(pct(regular, list.inside_pct), list) : null
  if (inside != null) lines.push(`Inside as well: ${list.inside_pct}% of the regular price`)

  const often = HOW_OFTEN.find((h) => h.value === job.how_often)!
  if (oftenPct(list, job.how_often) !== 100 && list.method !== 'by_area') lines.push(`${often.label}: ${oftenPct(list, job.how_often)}% of the price`)
  if (list.first_clean_extra_pct && list.method !== 'by_area') lines.push(`First clean: ${list.first_clean_extra_pct}% more${list.first_clean_gets_discount ? '' : ', with no how-often discount'}${list.first_clean_over_minimum ? ', on top of the smallest job too' : ''}`)
  const floor = job.fronts_only ? (list.fronts_minimum_pence ?? list.minimum_pence) : list.minimum_pence
  if (floor != null) lines.push(`Never less than ${money(floor)}${job.fronts_only ? ' for fronts only' : ''}`)
  if (list.round_to_pence) lines.push(`Rounded ${list.round_mode === 'up' ? 'up' : ''} to the nearest ${money(list.round_to_pence)}`.replace('  ', ' '))

  // No postcode while some areas have their own price: every figure is a range, never the default rate alone.
  let range: [number, number] | null = null
  let firstRange: [number, number] | null = null
  let insideRange: [number, number] | null = null
  if (lowBase != null && highBase != null) {
    // Each figure on its own: a minimum can make the two regular prices equal while the first cleans differ
    // (Astra, 1 Oct).
    const pair = (a: number, b: number): [number, number] | null => (a === b ? null : [a, b])
    const lowRegular = visit(lowBase, list, job, false)
    const highRegular = visit(highBase, list, job, false)
    range = pair(lowRegular, highRegular)
    firstRange = pair(visit(lowBase, list, job, true), visit(highBase, list, job, true))
    if (list.inside_pct > 0) insideRange = pair(roundPence(pct(lowRegular, list.inside_pct), list), roundPence(pct(highRegular, list.inside_pct), list))
  }
  return {
    status: missing.length ? 'needs_details' : 'priced',
    regular_pence: regular,
    first_pence: first,
    inside_pence: inside,
    lines,
    regular_range_pence: range,
    first_range_pence: firstRange,
    inside_range_pence: insideRange,
    missing,
  }
}

// Check a list sent by a screen before it is saved. Returns the cleaned
// list, or a sentence the owner can act on.
export function checkPriceList(input: unknown, sym = '£', country = 'GB'): { ok: true; list: PriceList } | { ok: false; error: string } {
  const area = AREA_WORDS[country] ?? AREA_WORDS.GB!
  if (!input || typeof input !== 'object') return { ok: false, error: 'No price list was sent.' }
  const i = input as Record<string, unknown>
  // first_clean_over_minimum is left off a blank list (and every list saved before), so it is named here.
  const known = new Set([...Object.keys(blankPriceList('per_window')), 'first_clean_over_minimum'])
  const unknown = Object.keys(i).filter((k) => !known.has(k))
  if (unknown.length) return { ok: false, error: `Unknown price setting: ${unknown[0]}.` }
  if (i.schema !== PRICE_LIST_SCHEMA) return { ok: false, error: 'This page is out of date. Reload it and try again.' }
  const money = (v: unknown, what: string, allowNull: boolean): number | null | string => {
    if (v == null && allowNull) return null
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > MAX_PENCE) return `${what} needs to be a price between ${sym}0 and ${sym}10,000.`
    return v
  }
  const percent = (v: unknown, what: string): number | string => {
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > MAX_PCT) return `${what} needs to be a whole number between 0 and 1000.`
    return v
  }
  const method = i.method
  if (method !== 'per_window' && method !== 'house_size' && method !== 'by_area') return { ok: false, error: 'Choose per window, by house size or by size in square metres.' }

  const windowPence = money(i.window_pence, 'The price per window', true)
  if (typeof windowPence === 'string') return { ok: false, error: windowPence }
  if (method === 'per_window' && windowPence == null) return { ok: false, error: 'Add your price per window.' }

  const areas: AreaPrice[] = []
  for (const a of Array.isArray(i.area_prices) ? i.area_prices : []) {
    if (!a || typeof a !== 'object') return { ok: false, error: 'An area price is missing its details.' }
    const r = a as Record<string, unknown>
    const p = money(r.window_pence, 'An area price', false)
    if (typeof p === 'string') return { ok: false, error: p }
    const typed = Array.isArray(r.starts) ? r.starts.map((s) => String(s).trim()).filter(Boolean) : []
    if (typed.length === 0) return { ok: false, error: area.none }
    const starts: string[] = []
    for (const t of typed) {
      // Refused, never quietly dropped: "N10, N*" must not save as just N10 (Astra, 1 Oct).
      const st = postcodeStart(t, country)
      if (!st) return { ok: false, error: area.bad(t) }
      starts.push(st)
    }
    areas.push({ starts: [...new Set(starts)], window_pence: p as number })
  }
  if (areas.length > 50) return { ok: false, error: 'That is more than 50 area prices.' }
  const seen = new Set<string>()
  for (const a of areas) for (const st of a.starts) {
    if (seen.has(st)) return { ok: false, error: `${st} is in two area prices. Keep it in one.` }
    seen.add(st)
  }

  const sizes: HouseSize[] = []
  for (const s of Array.isArray(i.sizes) ? i.sizes : []) {
    if (!s || typeof s !== 'object') return { ok: false, error: 'A house size is missing its details.' }
    const r = s as Record<string, unknown>
    const label = typeof r.label === 'string' ? r.label.trim().slice(0, 40) : ''
    const p = money(r.pence, `The price for ${label || 'a house size'}`, false)
    if (typeof p === 'string') return { ok: false, error: p }
    if (!label) return { ok: false, error: 'Each house size needs a name, like 3 bed.' }
    if ((p as number) === 0) return { ok: false, error: `Add a price for ${label}, or remove it.` }
    if (sizes.some((x) => x.label.toLowerCase() === label.toLowerCase())) return { ok: false, error: `${label} is in the list twice.` }
    sizes.push({ label, pence: p as number })
  }
  if (sizes.length > 20) return { ok: false, error: 'That is more than 20 house sizes.' }
  if (method === 'house_size' && sizes.filter((s) => s.pence > 0).length === 0) return { ok: false, error: 'Add a price for at least one house size.' }

  // A price a square metre for each kind of job (by_area); kept on any list, used only when that is how it prices.
  const sr = (i.size_rates ?? {}) as Record<string, unknown>
  const rates: SizeRates = { window: null, gutter: null, other: null }
  for (const k of ['window', 'gutter', 'other'] as const) {
    const v = money(sr[k], 'A price a square metre', true)
    if (typeof v === 'string') return { ok: false, error: v }
    rates[k] = v
  }
  if (method === 'by_area' && !Object.values(rates).some((v) => v != null && v > 0)) return { ok: false, error: 'Add a price a square metre for at least one kind of job.' }

  const firstExtra = percent(i.first_clean_extra_pct, 'First clean extra')
  if (typeof firstExtra === 'string') return { ok: false, error: firstExtra }
  const inside = percent(i.inside_pct, 'Inside price')
  if (typeof inside === 'string') return { ok: false, error: inside }
  const roof = money(i.roof_window_pence, 'The roof window price', true)
  if (typeof roof === 'string') return { ok: false, error: roof }
  const ho = (i.how_often_pct ?? {}) as Record<string, unknown>
  const howOften = {} as Record<HowOften, number>
  for (const h of HOW_OFTEN) {
    // 2 and 6 weeks missing (a list from before them, or an older phone): 4 and 8 weeks' share.
    const given = ho[h.value] ?? (h.value === 'w2' ? ho.w4 : h.value === 'w6' ? ho.w8 : undefined)
    const v = percent(given, h.label)
    if (typeof v === 'string') return { ok: false, error: v }
    howOften[h.value] = v
  }
  const minimum = money(i.minimum_pence, 'The minimum price', true)
  if (typeof minimum === 'string') return { ok: false, error: minimum }
  const frontsMinimum = money(i.fronts_minimum_pence, 'The fronts-only minimum', true)
  if (typeof frontsMinimum === 'string') return { ok: false, error: frontsMinimum }
  if (![0, 100, 500, 1000].includes(i.round_to_pence as number)) return { ok: false, error: `Round to ${sym}1, ${sym}5, ${sym}10 or not at all.` }
  if (i.round_mode !== 'up' && i.round_mode !== 'nearest') return { ok: false, error: 'Round up or to the nearest.' }

  return {
    ok: true,
    list: {
      schema: PRICE_LIST_SCHEMA,
      method,
      window_pence: windowPence,
      area_prices: areas,
      sizes,
      // Only on a list that uses them, so every list saved before reads back exactly as it was.
      ...(method === 'by_area' || Object.values(rates).some((v) => v != null) ? { size_rates: rates } : {}),
      first_clean_extra_pct: firstExtra,
      first_clean_gets_discount: i.first_clean_gets_discount === true,
      // Only when on, so every list saved before reads back exactly as it was.
      ...(i.first_clean_over_minimum === true ? { first_clean_over_minimum: true } : {}),
      inside_pct: inside,
      roof_window_pence: roof,
      how_often_pct: howOften,
      minimum_pence: minimum,
      fronts_minimum_pence: frontsMinimum,
      round_to_pence: i.round_to_pence as number,
      round_mode: i.round_mode,
    },
  }
}
